@import 'styles/mixins';
@import 'styles/variables';

.root {
  transition: all 0.2s ease-in-out, visibility 0s;
  -webkit-transition: all 0.2s ease-in-out, visibility 0s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 0;
  white-space: nowrap;
  position: relative;
  min-width: fit-content;

  &.fullWidth {
    width: 100%;
  }

  .frontIcon {
    min-width: 16px;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .endIcon {
    flex: 1;
    width: 16px;
    height: 16px;

    @include for-phone-only {
      flex: unset;
    }
  }

  &.disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  .chevron {
    fill: var(--neutral-0);

    &.up {
      transform: rotate(180deg);
    }
  }

  svg {
    flex-shrink: 0;
  }

  .label {
    display: flex;
    align-items: center;
    min-width: 0;
  }
}

// button is an <a /> tag
.link {
  &:hover {
    &.primary {
      color: white;
    }

    &.secondary {
      color: var(--action-60);
    }

    &.secondaryGrey {
      color: var(--neutral-50);
    }

    &.tertiary {
      color: var(--neutral-90);
    }

    &.tertiaryGreen {
      color: var(--action-60);
    }

    &.ghostWhite {
      color: $white;
    }
  }

  &:focus {
    &.primary {
      box-shadow: 0 0 0 3px rgba(102, 78, 255, 0.3), 0 1px 0 rgba(19, 21, 26, 0.12);

      [data-design-system='blaze'] & {
        box-shadow: none;
      }
    }

    &.secondary {
      box-shadow: 0 0 0 3px rgba(102, 78, 255, 0.2), 0 1px 2px $grey-5;

      [data-design-system='blaze'] & {
        box-shadow: none;
      }
    }

    &.ghostWhite {
      background-color: rgba(255, 255, 255, 0.2);
      box-shadow: 0px 0px 0px 2px rgba(255, 255, 255, 0.5);
    }
  }
}

// Sizes

.sm {
  @include sm-medium;
  padding: 6px 8px;
  border-radius: 6px;
  column-gap: 4px;
  min-height: 28px;

  &.withFrontIcon {
    padding-left: 6px;
  }

  &.withEndIcon {
    padding-right: 6px;
  }

  .label {
    [data-design-system='blaze'] & {
      padding-bottom: 1px;
      gap: 4px;
    }
  }
}

.md {
  @include md-medium;
  padding: 6px 10px;
  border-radius: 8px;
  column-gap: 6px;
  min-height: 32px;

  &.withFrontIcon {
    padding-left: 8px;
  }

  &.withEndIcon {
    padding-right: 8px;
  }

  .label {
    [data-design-system='blaze'] & {
      gap: 6px;
    }
  }
}

.lg {
  @include md-medium;
  padding: 10px 12px;
  min-height: 36px;
  border-radius: 8px;
  column-gap: 6px;

  &.withFrontIcon {
    padding-left: 10px;
  }

  &.withEndIcon {
    padding-right: 10px;
  }

  .label {
    [data-design-system='blaze'] & {
      gap: 2px;
    }
  }
}

.xl {
  @include md-medium;
  padding: 10px 16px;
  min-height: 44px;
  border-radius: 8px;
  column-gap: 6px;

  &.withFrontIcon {
    padding-left: 12px;
  }

  &.withEndIcon {
    padding-right: 12px;
  }

  .frontIcon {
    width: 24px;
    height: 24px;
  }

  .endIcon {
    width: 24px;
    height: 24px;
  }

  .label {
    [data-design-system='blaze'] & {
      padding-bottom: 2px;
      gap: 6px;
    }
  }
}

.xxl {
  @include lg-medium;
  padding: 12px;
  min-height: 48px;
  border-radius: 8px;
  column-gap: 6px;

  &.withFrontIcon {
    padding-left: 12px;
  }

  &.withEndIcon {
    padding-right: 12px;
  }

  .frontIcon,
  .endIcon {
    width: 24px;
    height: 24px;
  }

  .label {
    [data-design-system='blaze'] & {
      padding-bottom: 2px;
      gap: 6px;
    }
  }
}

// Variants

.primary {
  background: var(--action-50);
  color: $white;

  [data-design-system='blaze'] & {
    background: var(--dark-80);
    color: var(--light-100);
  }

  .endIcon,
  .frontIcon {
    fill: $white;
  }

  &.hovered {
    background: linear-gradient(0deg, rgba(19, 21, 26, 0.1), rgba(19, 21, 26, 0.1)), var(--action-50);

    [data-design-system='blaze'] & {
      color: var(--light-100);
      background: var(--selected-fill);
    }
  }

  &.pressed {
    background: linear-gradient(0deg, rgba(19, 21, 26, 0.2), rgba(19, 21, 26, 0.2)), var(--action-50);

    [data-design-system='blaze'] & {
      background: var(--selected-fill);
      color: var(--light-100);

      &:after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        background: var(--light-8);
        left: 0;
      }
    }
  }

  &.focused {
    box-shadow: 0 0 0 2px rgba(102, 78, 255, 0.3);
  }
}

.tertiary {
  color: var(--neutral-90);

  [data-design-system='blaze'] & {
    color: var(--dark-80);
  }

  .chevron {
    fill: var(--neutral-90);

    [data-design-system='blaze'] & {
      fill: var(--dark-80);
    }
  }

  .frontIcon,
  .endIcon {
    fill: var(--neutral-40);

    [data-design-system='blaze'] & {
      fill: var(--dark-80);
    }
  }

  &.hovered {
    background: var(--neutral-5);

    [data-design-system='blaze'] & {
      background: var(--dark-4);
    }
  }

  &.pressed {
    background: var(--neutral-10);

    [data-design-system='blaze'] & {
      background: var(--dark-8);

      &:after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        background: var(--light-8);
        left: 0;
      }
    }
  }

  &.focused {
    box-shadow: 0 0 0 2px var(--neutral-30);

    [data-design-system='blaze'] & {
      box-shadow: none;
    }
  }
}

html:not([data-design-system='blaze']) .tertiaryGreen {
  @extend .tertiary;

  color: var(--action-60);

  .frontIcon,
  .endIcon {
    fill: var(--action-60);
  }

  &.focused {
    box-shadow: 0 0 0 2px var(--action-10);
  }
}

[data-design-system='blaze'] .tertiaryGreen {
  @extend .tertiary;
}

// Secondary Action
.secondary {
  position: relative;
  background: var(--neutral-0);
  box-shadow: inset 0 0 0 1px var(--neutral-10), 0px 1px 2px 0px #f7f8f9;
  color: var(--action-60);

  [data-design-system='blaze'] & {
    background: var(--light-100);
    color: var(--dark-80);
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.07), 0px 0px 4px 0px rgba(0, 0, 0, 0.03);
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--neutral-5);
    opacity: 0;
    transition: opacity 0.2s ease;
    border-radius: inherit;

    [data-design-system='blaze'] & {
      display: none;
    }
  }

  .frontIcon,
  .endIcon {
    fill: var(--action-60);

    [data-design-system='blaze'] & {
      fill: var(--dark-80);
    }
  }

  &.hovered {
    &:after {
      opacity: 1;
    }

    [data-design-system='blaze'] & {
      background: var(--dark-4);
    }

    .frontIcon,
    .endIcon {
      [data-design-system='blaze'] & {
        fill: var(--dark-80);
      }
    }
  }

  &.pressed {
    background: var(--neutral-10);

    [data-design-system='blaze'] & {
      background: var(--dark-4);

      &:after {
        content: '';
        display: initial;
        position: absolute;
        width: 100%;
        height: 100%;
        background: var(--light-8);
        left: 0;
      }
    }
  }

  &.focused {
    box-shadow: 0 0 0 3px var(--action-10), 0 1px 2px var(--neutral-5);

    [data-design-system='blaze'] & {
      box-shadow: none;
    }
  }

  [data-theme='dark'] & {
    box-shadow: inset 0 0 0 1px var(--neutral-10);
  }
}

html:not([data-design-system='blaze']) .secondaryGrey {
  @extend .secondary;
  color: var(--neutral-50);
  box-shadow: none;
  background-color: transparent;

  .frontIcon,
  .endIcon {
    fill: var(--neutral-40);
  }

  .chevron {
    fill: var(--neutral-40);
  }
}

[data-design-system='blaze'] .secondaryGrey {
  @extend .secondary;
}

.ghostWhite {
  background: rgba(255, 255, 255, 0.1);
  color: $white;

  .frontIcon,
  .endIcon {
    fill: $white;
  }

  &.hovered {
    background: rgba(255, 255, 255, 0.2);
  }

  &.pressed {
    background: rgba(255, 255, 255, 0.3);
  }

  &.focused {
    background-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0px 0px 0px 2px rgba(255, 255, 255, 0.5);
  }
}

.ghostDark {
  background: transparent;
  color: var(--action-40);

  .frontIcon,
  .endIcon {
    fill: var(--action-40);
  }

  &.hovered {
    background: rgba(255, 255, 255, 0.05);
  }

  &.pressed {
    background: rgba(255, 255, 255, 0.1);
  }

  &.focused {
    background-color: rgba(255, 255, 255, 0.05);
    box-shadow: 0px 0px 0px 2px rgba(255, 255, 255, 0.01);
  }
}

.toggleOff {
  @extend .tertiary;
}

.toggleOn {
  @extend .tertiary;
  background: var(--neutral-5);

  [data-design-system='blaze'] & {
    background: var(--dark-4);
  }
}

.secondaryChip {
  background: $white;
  border: 1px solid $grey-10;
  border-radius: 23px;

  .frontIcon,
  .endIcon {
    fill: $grey-50;
  }

  &.hovered {
    background: $grey-5;
  }

  &.pressed {
    background: $grey-10;
  }

  &.focused {
    box-shadow: 0 0 0 3px $grey-20, 0 1px 2px $grey-5;
  }

  .chevron {
    fill: $grey-50;
  }
}

// UNCONFIRMED VARIANTS
/* stylelint-disable css-modules/no-global-scoped-selector */
@mixin blaze-button {
  color: var(--White, #fff);
  font-family: Roboto Flex;
  font-size: 16px;
  font-style: normal;
  font-weight: 800;
  line-height: 142%;
  display: inline-flex;
  padding: 8px 11px;
  justify-content: center;
  align-items: center;
  position: relative;
  @include transition(100ms);

  [data-design-system='blaze'] & {
    font-family: Sohne;
    font-weight: 700;
  }

  &:after {
    content: '';
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0;
    @include transition(100ms);
  }

  &.hovered {
    &:after {
      background-color: var(--neutral-0);
      opacity: 0.1;

      [data-design-system='blaze'] & {
        background-color: var(--light-100);
      }
    }
  }

  &.pressed {
    &:after {
      background-color: var(--neutral-90);
      opacity: 0.2;

      [data-design-system='blaze'] & {
        background-color: var(--dark-80);
      }
    }
  }

  // This variant does not support different sizes
  &.sm,
  &.md,
  &.lg,
  &.xl,
  &.xxl {
    min-height: 40px;
    padding: 8px 16px;
    column-gap: 4px;
    font-size: 16px;

    &.withFrontIcon {
      padding-left: 12px;
    }

    &.withEndIcon {
      padding-right: 12px;
    }
  }
}

/* stylelint-enable css-modules/no-global-scoped-selector */

.blazePrimary {
  @include blaze-button;

  border-radius: 8px;
  border: 1px solid var(--neutral-90);
  background: var(--Action-50, #664eff);
  box-shadow: 3px 3px 0px -0.5px rgba(0, 0, 0, 0.1), 1px 1px 0px 1px #cec7ff inset;

  [data-design-system='blaze'] & {
    border: 1px solid var(--dark-80);
    background: var(--dark-80);
    color: var(--light-100);
    box-shadow: 3px 3px 0px -0.5px rgba(0, 0, 0, 0.1), 1px 1px 0px 1px #959595 inset;
  }

  .endIcon,
  .frontIcon {
    fill: var(--neutral-0);

    [data-design-system='blaze'] & {
      fill: var(--light-100);
    }
  }
}

.blazeSecondary {
  @include blaze-button;

  border-radius: 8px;
  border: 1px solid var(--neutral-90);
  background: #2b2f38;
  box-shadow: 3px 3px 0px -0.5px rgba(0, 0, 0, 0.1), 1px 1px 0 0px rgba(255, 255, 255, 0.7) inset;

  [data-design-system='blaze'] & {
    border: 1px solid var(--dark-80);
    background: var(--dark-80);
    color: var(--light-100);
    box-shadow: 3px 3px 0px -0.5px rgba(0, 0, 0, 0.1), 1px 1px 0px 1px #959595 inset;
  }

  .endIcon,
  .frontIcon {
    fill: var(--neutral-0);

    [data-design-system='blaze'] & {
      fill: var(--light-100);
    }
  }
}

[data-design-system='blaze'] .blazeSecondary {
  @extend .blazePrimary;
}

.blazeYellow {
  @include blaze-button;

  color: var(--neutral-90);
  border-radius: 8px;
  border: 1px solid var(--neutral-90);
  background: #ffdb59;
  box-shadow: 3px 3px 0px -0.5px rgba(0, 0, 0, 0.1), 1px 1px 0 0px rgba(255, 255, 255, 0.7) inset;

  .endIcon,
  .frontIcon {
    fill: var(--neutral-0);
  }
}
